import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { API_URL } from "../config/api";
import {
  Mic,
  MicOff,
  Square,
  Play,
  Pause,
  Trash2,
  Link2,
  FileText,
  Clock,
  Globe,
  RefreshCw,
  Search,
  ChevronRight,
  Volume2,
  Upload,
  Sparkles,
  Edit3,
  Save,
  X,
  FolderOpen,
  Truck,
  Newspaper,
  CheckCircle
} from "lucide-react";


// Link type labels
const LINK_TYPES = {
  pf: { label: "Position Folder", icon: FolderOpen, color: "blue" },
  to: { label: "Transport Order", icon: Truck, color: "green" },
  newsletter: { label: "Newsletter", icon: Newspaper, color: "purple" }
};

export default function VoiceNotesPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  
  // Media recorder refs
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);
  
  // Voice notes list
  const [voiceNotes, setVoiceNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Upload/transcribe state
  const [uploading, setUploading] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [noteTitle, setNoteTitle] = useState("");
  
  // View/edit state
  const [selectedNote, setSelectedNote] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  
  // Link modal state
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkingNote, setLinkingNote] = useState(null);
  const [linkType, setLinkType] = useState("pf");
  const [linkId, setLinkId] = useState("");

  // Fetch voice notes
  const fetchVoiceNotes = async () => {
    try {
      const response = await axios.get(`${API_URL}/voice-notes/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { search: searchQuery || undefined }
      });
      setVoiceNotes(response.data.voice_notes || []);
    } catch (error) {
      console.error("Error fetching voice notes:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchVoiceNotes();
    }
  }, [token, searchQuery]);

  // Recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        }
      });
      streamRef.current = stream;
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { 
          type: mediaRecorder.mimeType 
        });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
      };
      
      mediaRecorder.start(1000); // Collect data every second
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error("Error starting recording:", error);
      toast.error("Could not access microphone. Please allow microphone access.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      streamRef.current?.getTracks().forEach(track => track.stop());
      clearInterval(timerRef.current);
      setIsRecording(false);
      setIsPaused(false);
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      if (isPaused) {
        mediaRecorderRef.current.resume();
        timerRef.current = setInterval(() => {
          setRecordingTime(prev => prev + 1);
        }, 1000);
      } else {
        mediaRecorderRef.current.pause();
        clearInterval(timerRef.current);
      }
      setIsPaused(!isPaused);
    }
  };

  const discardRecording = () => {
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingTime(0);
    setNoteTitle("");
  };

  // Upload and transcribe
  const uploadAndTranscribe = async () => {
    if (!audioBlob) {
      toast.error("No recording to upload");
      return;
    }

    setTranscribing(true);
    try {
      const formData = new FormData();
      const filename = `recording_${Date.now()}.webm`;
      formData.append("audio", audioBlob, filename);
      
      const response = await axios.post(
        `${API_URL}/voice-notes/transcribe`,
        formData,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          },
          params: {
            title: noteTitle || undefined,
            auto_translate: true,
            generate_summary_flag: true
          }
        }
      );
      
      toast.success("Voice note transcribed successfully!");
      setAudioBlob(null);
      setAudioUrl(null);
      setNoteTitle("");
      setRecordingTime(0);
      fetchVoiceNotes();
      setSelectedNote(response.data);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Transcription failed");
    } finally {
      setTranscribing(false);
    }
  };

  // Upload existing file
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("audio", file);
      
      const response = await axios.post(
        `${API_URL}/voice-notes/transcribe`,
        formData,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          },
          params: {
            auto_translate: true,
            generate_summary_flag: true
          }
        }
      );
      
      toast.success("File transcribed successfully!");
      fetchVoiceNotes();
      setSelectedNote(response.data);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  // Delete note
  const handleDelete = async (noteId) => {
    if (!window.confirm("Delete this voice note?")) return;
    
    try {
      await axios.delete(`${API_URL}/voice-notes/${noteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Voice note deleted");
      fetchVoiceNotes();
      if (selectedNote?.id === noteId) {
        setSelectedNote(null);
      }
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  // Update note
  const handleUpdateNote = async () => {
    if (!editingNote) return;
    
    try {
      await axios.put(
        `${API_URL}/voice-notes/${editingNote.id}`,
        {
          title: editingNote.title,
          transcript: editingNote.transcript,
          summary: editingNote.summary
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Note updated");
      setSelectedNote(editingNote);
      setEditingNote(null);
      fetchVoiceNotes();
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  // Link note
  const handleLinkNote = async () => {
    if (!linkingNote || !linkId) return;
    
    try {
      await axios.post(
        `${API_URL}/voice-notes/${linkingNote.id}/link`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { link_type: linkType, link_id: linkId }
        }
      );
      toast.success(`Linked to ${LINK_TYPES[linkType].label}`);
      setShowLinkModal(false);
      setLinkingNote(null);
      setLinkId("");
      fetchVoiceNotes();
    } catch (error) {
      toast.error("Failed to link");
    }
  };

  // Regenerate summary
  const handleRegenerateSummary = async (noteId) => {
    try {
      const response = await axios.post(
        `${API_URL}/voice-notes/${noteId}/regenerate-summary`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Summary regenerated");
      if (selectedNote?.id === noteId) {
        setSelectedNote(prev => ({ ...prev, summary: response.data.summary }));
      }
      fetchVoiceNotes();
    } catch (error) {
      toast.error("Failed to regenerate summary");
    }
  };

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-12 text-center">
          <Mic className="w-16 h-16 mx-auto text-blue-500 mb-4" />
          <h1 className="text-2xl font-bold text-foreground mb-2">Voice Notes</h1>
          <p className="text-muted-foreground mb-6">Please log in to access Voice Notes.</p>
          <Button onClick={() => navigate("/login")}>Log In</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl">
              <Mic className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Voice Notes</h1>
              <p className="text-slate-500">Record and transcribe sales meetings</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search notes..."
                className="pl-10 w-64"
              />
            </div>
            
            <label className="cursor-pointer">
              <input
                type="file"
                accept="audio/*"
                onChange={handleFileUpload}
                className="hidden"
              />
              <Button variant="outline" disabled={uploading} asChild>
                <span>
                  {uploading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Upload Audio
                </span>
              </Button>
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Recording & Notes List */}
          <div className="lg:col-span-1 space-y-6">
            {/* Recording Card */}
            <motion.div
              className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="p-4 border-b border-slate-100 bg-gradient-to-r from-blue-50 to-indigo-50">
                <h2 className="font-semibold text-slate-800 flex items-center gap-2">
                  <Volume2 className="w-5 h-5 text-blue-600" />
                  Record Voice Note
                </h2>
              </div>
              
              <div className="p-6">
                {/* Recording visualization */}
                <div className="flex flex-col items-center mb-6">
                  <div className={`w-32 h-32 rounded-full flex items-center justify-center transition-all ${
                    isRecording 
                      ? isPaused 
                        ? 'bg-amber-100 border-4 border-amber-300' 
                        : 'bg-red-100 border-4 border-red-400 animate-pulse'
                      : audioBlob 
                        ? 'bg-green-100 border-4 border-green-300'
                        : 'bg-slate-100 border-4 border-slate-200'
                  }`}>
                    {isRecording ? (
                      <div className="text-center">
                        <Mic className={`w-10 h-10 mx-auto ${isPaused ? 'text-amber-500' : 'text-red-500'}`} />
                        <span className="text-2xl font-mono font-bold text-slate-800 mt-2 block">
                          {formatTime(recordingTime)}
                        </span>
                      </div>
                    ) : audioBlob ? (
                      <CheckCircle className="w-12 h-12 text-green-500" />
                    ) : (
                      <Mic className="w-12 h-12 text-slate-400" />
                    )}
                  </div>
                  
                  {!isRecording && !audioBlob && (
                    <p className="text-sm text-slate-500 mt-3 text-center">
                      Tap to start recording
                    </p>
                  )}
                  
                  {audioBlob && !isRecording && (
                    <p className="text-sm text-green-600 mt-3 font-medium">
                      Recording ready ({formatTime(recordingTime)})
                    </p>
                  )}
                </div>

                {/* Recording controls */}
                <div className="flex justify-center gap-3 mb-4">
                  {!isRecording && !audioBlob && (
                    <Button
                      onClick={startRecording}
                      size="lg"
                      className="bg-red-500 hover:bg-red-600 rounded-full w-16 h-16"
                    >
                      <Mic className="w-6 h-6" />
                    </Button>
                  )}
                  
                  {isRecording && (
                    <>
                      <Button
                        onClick={pauseRecording}
                        variant="outline"
                        size="lg"
                        className="rounded-full w-14 h-14"
                      >
                        {isPaused ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
                      </Button>
                      <Button
                        onClick={stopRecording}
                        size="lg"
                        className="bg-slate-800 hover:bg-slate-900 rounded-full w-14 h-14"
                      >
                        <Square className="w-5 h-5" />
                      </Button>
                    </>
                  )}
                  
                  {audioBlob && !isRecording && (
                    <>
                      <Button
                        onClick={discardRecording}
                        variant="outline"
                        size="lg"
                        className="rounded-full w-14 h-14 border-red-200 text-red-500 hover:bg-red-50"
                      >
                        <Trash2 className="w-5 h-5" />
                      </Button>
                      <Button
                        onClick={startRecording}
                        variant="outline"
                        size="lg"
                        className="rounded-full w-14 h-14"
                      >
                        <RefreshCw className="w-5 h-5" />
                      </Button>
                    </>
                  )}
                </div>

                {/* Audio preview */}
                {audioUrl && (
                  <div className="mb-4">
                    <audio src={audioUrl} controls className="w-full" />
                  </div>
                )}

                {/* Title & Upload */}
                {audioBlob && (
                  <div className="space-y-3">
                    <Input
                      value={noteTitle}
                      onChange={(e) => setNoteTitle(e.target.value)}
                      placeholder="Note title (optional)"
                    />
                    <Button
                      onClick={uploadAndTranscribe}
                      disabled={transcribing}
                      className="w-full bg-gradient-to-r from-blue-600 to-indigo-600"
                    >
                      {transcribing ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Transcribing...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Transcribe & Save
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Notes List */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="p-4 border-b border-slate-100">
                <h2 className="font-semibold text-slate-800 flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-slate-600" />
                    Recent Notes
                  </span>
                  <span className="text-sm text-slate-500">{voiceNotes.length} total</span>
                </h2>
              </div>
              
              <div className="max-h-[400px] overflow-y-auto">
                {loading ? (
                  <div className="p-8 text-center">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                  </div>
                ) : voiceNotes.length === 0 ? (
                  <div className="p-8 text-center">
                    <Mic className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                    <p className="text-slate-500">No voice notes yet</p>
                    <p className="text-sm text-slate-400">Record your first note above</p>
                  </div>
                ) : (
                  voiceNotes.map((note) => (
                    <div
                      key={note.id}
                      onClick={() => setSelectedNote(note)}
                      className={`p-4 border-b border-slate-100 cursor-pointer transition-colors hover:bg-slate-50 ${
                        selectedNote?.id === note.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-slate-800 truncate">{note.title}</h3>
                          <p className="text-sm text-slate-500 line-clamp-2 mt-1">
                            {note.summary || note.transcript?.slice(0, 100)}...
                          </p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {new Date(note.created_at).toLocaleDateString()}
                            </span>
                            {note.detected_language && (
                              <span className="flex items-center gap-1">
                                <Globe className="w-3 h-3" />
                                {note.detected_language}
                              </span>
                            )}
                            {note.link_type && (
                              <span className={`px-1.5 py-0.5 rounded text-${LINK_TYPES[note.link_type]?.color || 'slate'}-600 bg-${LINK_TYPES[note.link_type]?.color || 'slate'}-100`}>
                                {LINK_TYPES[note.link_type]?.label}
                              </span>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-400 mt-1" />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Note Detail */}
          <div className="lg:col-span-2">
            {selectedNote ? (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden h-full"
              >
                {/* Note Header */}
                <div className="p-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-blue-50">
                  <div className="flex items-center justify-between">
                    {editingNote ? (
                      <Input
                        value={editingNote.title}
                        onChange={(e) => setEditingNote(prev => ({ ...prev, title: e.target.value }))}
                        className="text-lg font-semibold"
                      />
                    ) : (
                      <h2 className="text-lg font-semibold text-slate-800">{selectedNote.title}</h2>
                    )}
                    <div className="flex items-center gap-2">
                      {editingNote ? (
                        <>
                          <Button size="sm" variant="outline" onClick={() => setEditingNote(null)}>
                            <X className="w-4 h-4" />
                          </Button>
                          <Button size="sm" onClick={handleUpdateNote}>
                            <Save className="w-4 h-4 mr-1" />
                            Save
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => setEditingNote({ ...selectedNote })}
                          >
                            <Edit3 className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              setLinkingNote(selectedNote);
                              setShowLinkModal(true);
                            }}
                          >
                            <Link2 className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-red-500 hover:bg-red-50"
                            onClick={() => handleDelete(selectedNote.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Meta info */}
                  <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {new Date(selectedNote.created_at).toLocaleString()}
                    </span>
                    {selectedNote.duration_seconds && (
                      <span>Duration: {formatTime(Math.round(selectedNote.duration_seconds))}</span>
                    )}
                    {selectedNote.detected_language && (
                      <span className="flex items-center gap-1">
                        <Globe className="w-4 h-4" />
                        {selectedNote.detected_language}
                      </span>
                    )}
                    {selectedNote.link_type && (
                      <span className={`px-2 py-0.5 rounded-full text-xs bg-${LINK_TYPES[selectedNote.link_type]?.color}-100 text-${LINK_TYPES[selectedNote.link_type]?.color}-700`}>
                        Linked to {LINK_TYPES[selectedNote.link_type]?.label}: {selectedNote.link_id}
                      </span>
                    )}
                  </div>
                </div>

                {/* Note Content */}
                <div className="p-6 space-y-6 max-h-[600px] overflow-y-auto">
                  {/* Summary */}
                  {(selectedNote.summary || editingNote) && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-slate-700 font-medium flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-purple-500" />
                          AI Summary
                        </Label>
                        {!editingNote && (
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => handleRegenerateSummary(selectedNote.id)}
                          >
                            <RefreshCw className="w-3 h-3 mr-1" />
                            Regenerate
                          </Button>
                        )}
                      </div>
                      {editingNote ? (
                        <Textarea
                          value={editingNote.summary || ""}
                          onChange={(e) => setEditingNote(prev => ({ ...prev, summary: e.target.value }))}
                          className="min-h-[100px]"
                        />
                      ) : (
                        <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                          <p className="text-slate-700 whitespace-pre-wrap">{selectedNote.summary}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Transcript */}
                  <div>
                    <Label className="text-slate-700 font-medium flex items-center gap-2 mb-2">
                      <FileText className="w-4 h-4 text-blue-500" />
                      Transcript (English)
                    </Label>
                    {editingNote ? (
                      <Textarea
                        value={editingNote.transcript || ""}
                        onChange={(e) => setEditingNote(prev => ({ ...prev, transcript: e.target.value }))}
                        className="min-h-[300px]"
                      />
                    ) : (
                      <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                        <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">
                          {selectedNote.transcript}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Original transcript (if different) */}
                  {selectedNote.original_transcript && (
                    <div>
                      <Label className="text-slate-700 font-medium flex items-center gap-2 mb-2">
                        <Globe className="w-4 h-4 text-green-500" />
                        Original ({selectedNote.detected_language})
                      </Label>
                      <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                        <p className="text-slate-700 whitespace-pre-wrap leading-relaxed text-sm">
                          {selectedNote.original_transcript}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12 text-center h-full flex flex-col items-center justify-center">
                <Mic className="w-16 h-16 text-slate-300 mb-4" />
                <h3 className="text-xl font-semibold text-slate-600 mb-2">Select a Voice Note</h3>
                <p className="text-slate-400 max-w-md">
                  Record a new voice note or select an existing one from the list to view its transcript and summary.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Link Modal */}
      <AnimatePresence>
        {showLinkModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={(e) => e.target === e.currentTarget && setShowLinkModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl w-full max-w-md p-6"
            >
              <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Link2 className="w-5 h-5 text-blue-600" />
                Link Voice Note
              </h3>
              
              <div className="space-y-4">
                <div>
                  <Label>Link Type</Label>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {Object.entries(LINK_TYPES).map(([key, val]) => {
                      const Icon = val.icon;
                      return (
                        <button
                          key={key}
                          onClick={() => setLinkType(key)}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            linkType === key 
                              ? `border-${val.color}-500 bg-${val.color}-50` 
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <Icon className={`w-5 h-5 mx-auto mb-1 text-${val.color}-600`} />
                          <span className="text-xs font-medium">{val.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                <div>
                  <Label>ID / Reference</Label>
                  <Input
                    value={linkId}
                    onChange={(e) => setLinkId(e.target.value)}
                    placeholder={`Enter ${LINK_TYPES[linkType]?.label} ID...`}
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <Button variant="outline" onClick={() => setShowLinkModal(false)}>
                  Cancel
                </Button>
                <Button onClick={handleLinkNote} disabled={!linkId}>
                  <Link2 className="w-4 h-4 mr-2" />
                  Link
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
